import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Button,
  closeCurrentDialog,
  DataStatus,
  FormSubmitHandler,
  openDeleteDialog,
  openDialog,
  showNotification,
} from 'platform/components';
import {Heading, HStack, VStack} from 'platform/foundation';
import {formatPhoneNumber} from 'platform/locale';

// eslint-disable-next-line no-restricted-imports -- Intended
import {always, ifElse, pathOr, pipe} from 'ramda';
import {isNotNil, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  CustomerResponseBodyV2,
  PersonRequestBody,
  PersonResponseBodyV2,
  useCreateCustomerContactV2Mutation,
  useDeleteCustomerContactMutation,
  usePatchCustomerContactV2Mutation,
} from '@dms/api/customer';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {ApiError, suffixTestId, TestIdProps} from 'shared/web';

import {useCustomerPersonAttributes} from '../../../hooks/useCustomerPersonAttributes';
import {getContactPersonRoleTypeName} from '../../../utils/getContactPersonRoleTypeName';
import {getContactRequestBody} from '../../../utils/getContactRequestBody';
import {getEmailParameter} from '../../../utils/getEmailParameter';
import {getNaturalPersonFullName} from '../../../utils/getNaturalPersonFullName';
import {handleApiError} from '../../../utils/handleApiError';
import {handleContactApiError} from '../../../utils/handleContactApiError';
import {InfoCard} from '../../InfoCard/InfoCard';
import {NoPermissionTooltip} from '../../NoPermissionTooltip/NoPermissionTooltip';
import {SecondStepComponentType} from '../types/SecondStepComponentType';
import {ContactPersonForm} from './ContactPersonForm';

interface CustomerContactPersonProps extends TestIdProps {
  secondStepComponentType: SecondStepComponentType;
  customer: CustomerResponseBodyV2;
  isEditingDisabled?: boolean;
}

export function CustomerContactPerson(props: CustomerContactPersonProps) {
  const [getAttributes] = useCustomerPersonAttributes(
    props.secondStepComponentType === 'SERVICE_CASE'
  );

  const [createContact, {isLoading: isCreateContactLoading}] = useCreateCustomerContactV2Mutation();
  const [editContact, {isLoading: isEditContactLoading}] = usePatchCustomerContactV2Mutation();
  const [deleteContact] = useDeleteCustomerContactMutation();

  const onCreateSubmit: FormSubmitHandler<PersonRequestBody> = async (values, setErrors) => {
    const {personRequestBody, emailFormIndexes} = getContactRequestBody(values);

    await createContact({customerId: props.customer.id, personRequestBody})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.success'));
        closeCurrentDialog();
      })
      .catch((error: ApiError) => handleContactApiError(error, setErrors, emailFormIndexes));
  };

  const onEditSubmit =
    (contactId: string): FormSubmitHandler<PersonRequestBody> =>
    async (values, setErrors) => {
      const {personRequestBody, emailFormIndexes} = getContactRequestBody(values);

      await editContact({customerId: props.customer.id, contactId, personRequestBody})
        .unwrap()
        .then(() => {
          showNotification.success(i18n.t('general.notifications.success'));
          closeCurrentDialog();
        })
        .catch((error: ApiError) => handleContactApiError(error, setErrors, emailFormIndexes));
    };

  const onDelete = (contactId: string) =>
    openDeleteDialog({
      onConfirm: () =>
        deleteContact({
          customerId: props.customer.id,
          contactId,
        })
          .unwrap()
          .catch(handleApiError),
    });

  const getPhoneNumber = pipe(
    pathOr(null, ['phoneNumbers', 0]),
    ifElse(
      isNotNilOrEmpty,
      (value) => formatPhoneNumber(`${value.prefix}${value.number}`),
      always(i18n.t('entity.customer.labels.noPhoneNumber'))
    )
  );

  const getEmail = pipe(
    pathOr(null, ['emails', 0]),
    ifElse(
      isNotNilOrEmpty,
      (value) => getEmailParameter(value.email),
      always(i18n.t('entity.customer.labels.noEmail'))
    )
  );

  const isLastContactInBusiness =
    props.customer.discriminator === 'BUSINESS' && props.customer.contacts.length === 1;

  const getActions = (contact: PersonResponseBodyV2): Action[] => [
    {
      type: 'iconButtonWithTooltip',
      severity: 'danger',
      icon: 'action/delete',
      isDisabled: isLastContactInBusiness || props.isEditingDisabled,
      onClick: () => onDelete(contact.id),
      tooltip: {
        label: props.isEditingDisabled ? i18n.t('general.labels.noPermission') : undefined,
        isDisabled: !props.isEditingDisabled,
      },
    },
    {
      type: 'iconButtonWithTooltip',
      icon: 'image/edit',
      isDisabled: props.isEditingDisabled,
      onClick: () =>
        openDialog(
          <ContactPersonForm
            customerId={props.customer.id}
            isLoading={isEditContactLoading}
            contact={contact}
            onSubmit={onEditSubmit(contact.id)}
            data-testid={suffixTestId('editContact', props)}
          />,
          {
            title: i18n.t('entity.customer.labels.editContactPerson'),
            withAdditionalFooter: true,
            'data-testid': suffixTestId('editContact', props),
          }
        ),
      tooltip: {
        label: props.isEditingDisabled ? i18n.t('general.labels.noPermission') : undefined,
        isDisabled: !props.isEditingDisabled,
      },
    },
  ];

  const handleAddClick = () =>
    openDialog(
      <ContactPersonForm
        customerId={props.customer.id}
        isLoading={isCreateContactLoading}
        onSubmit={onCreateSubmit}
        data-testid={suffixTestId('createContact', props)}
      />,
      {
        title: i18n.t('entity.customer.actions.newContactPerson'),
        withAdditionalFooter: true,
        'data-testid': suffixTestId('createContact', props),
      }
    );

  return (
    <>
      <HStack justify="space-between">
        <Heading size={5}>{i18n.t('entity.customer.labels.contactPersons')}</Heading>
        <NoPermissionTooltip shouldShowTooltip={Boolean(props.isEditingDisabled)}>
          <Button
            title={i18n.t('general.actions.add')}
            variant="link"
            size="small"
            leftIcon="content/add_circle"
            onClick={handleAddClick}
            isDisabled={props.isEditingDisabled}
            data-testid={suffixTestId('addContact', props)}
          />
        </NoPermissionTooltip>
      </HStack>
      <DataStatus
        isEmpty={!props.customer.contacts.length}
        emptyMessage={i18n.t('entity.customer.labels.noContactPerson')}
        minHeight={23}
      >
        <VStack spacing={4}>
          {props.customer.contacts.map((contact: PersonResponseBodyV2, index) => (
            <InfoCard
              key={contact.id}
              title={getNaturalPersonFullName(contact)}
              subTitle={contact.roles}
              isPrimary={
                isFeatureEnabled(featureFlags.CUSTOMER_PRIMARY_CONTACT) &&
                contact.id === props.customer.primaryContactId
              }
              flags={
                isNotNil(contact.roleType)
                  ? [
                      {
                        label: getContactPersonRoleTypeName(i18n, contact.roleType) ?? '',
                        colorScheme: 'neutral',
                        isSubtle: true,
                      },
                    ]
                  : undefined
              }
              description={[getPhoneNumber(contact), getEmail(contact)]}
              actions={getActions(contact)}
              attributes={getAttributes(contact)}
              data-testid={suffixTestId(`contact-${index}`, props)}
              selectedContactId={contact.id}
            />
          ))}
        </VStack>
      </DataStatus>
    </>
  );
}
